import { isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpCacheManager } from '@ngneat/cashew';
import { firstValueFrom } from 'rxjs';
import {
  AppUpdateService,
  logAppUpdate,
} from '@/services/app-update/app-update.service';
import { AppDeploymentService } from '@/shared/openapi/api/app-deployment.service';
import { WebSocketService } from '@/core/services/websocket.service';

/** Response shape from app-deployment getByApplication (application=1 → web app). */
interface AppDeploymentResponse {
  webAppVersion?: string;
  webAppBuildDate?: number;
  pwaVersion?: string;
  pwaBuildDate?: number;
}

/** Latest constructed service. Older test instances ignore window events. */
let latestServiceId = 0;

@Injectable({
  providedIn: 'root',
})
export class APIVersionService {
  static readonly VERSION_STORAGE_KEY = 'version-omm';
  /** Cashew key prefixes: @cache-, @ttl-, @version- */
  private static readonly CASHEW_KEY_PREFIXES = [
    '@cache-',
    '@ttl-',
    '@version-',
  ] as const;
  private readonly appDeploymentService = inject(AppDeploymentService);
  private readonly httpCacheManager = inject(HttpCacheManager);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly webSocket = inject(WebSocketService);
  private readonly appUpdate = inject(AppUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly serviceId = ++latestServiceId;

  private readonly _version = signal<string | null>(null);
  private readonly _buildDate = signal<number | null>(null);
  readonly updateAvailable = signal(false);

  public readonly version = computed(() => this._version());
  public readonly buildDate = computed(() => this._buildDate());

  /** Version this document loaded with. Unchanged until a full reload. */
  private sessionVersion: string | null = null;
  private pendingRemote: string | null = null;
  private fetchPromise: Promise<string> | null = null;
  private watching = false;

  /**
   * Fetches the web-app deployment version from the API.
   * Clears Cashew cache when the version changes.
   * The first call freezes sessionVersion.
   */
  public async getVersion(): Promise<string> {
    if (this._version()) {
      return this._version()!;
    }
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = this.fetchVersion();
    try {
      return await this.fetchPromise;
    } finally {
      this.fetchPromise = null;
    }
  }

  /**
   * Always hits the network. Compares to sessionVersion, not localStorage.
   */
  public async checkForUpdate(): Promise<void> {
    if (!this.sessionVersion) {
      return;
    }
    try {
      const remote = await this.readDeployment();
      this.noteRemoteVersion(remote.version);
    } catch (error) {
      console.error('Failed to check app version:', error);
    }
  }

  /** Socket, focus/reconnect catch-up, and chunk-load detection. Call once from the app shell. */
  public watchForDeploy(): void {
    if (this.watching || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.watching = true;
    const abort = new AbortController();
    this.destroyRef.onDestroy(() => abort.abort());

    this.webSocket
      .appVersionChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(message => {
        logAppUpdate('app_version_changed', {
          message,
          session: this.sessionVersion,
        });
        if (!this.alive() || Number(message.application) !== 1) {
          return;
        }
        this.noteRemoteVersion(message.version);
      });

    this.webSocket
      .socketConnected()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.catchUp());

    window.addEventListener('focus', this.onFocus, { signal: abort.signal });
    window.addEventListener('error', this.onWindowError, {
      signal: abort.signal,
    });
    window.addEventListener('unhandledrejection', this.onRejection, {
      signal: abort.signal,
    });
  }

  private onFocus = (): void => {
    this.catchUp();
  };

  private catchUp(): void {
    if (!this.alive()) {
      return;
    }
    void this.checkForUpdate();
  }

  private onWindowError = (event: ErrorEvent): void => {
    if (!this.alive()) {
      return;
    }
    if (isChunkLoadFailure(event.error) || isChunkLoadFailure(event.message)) {
      this.markRemote();
    }
  };

  private onRejection = (event: PromiseRejectionEvent): void => {
    if (!this.alive()) {
      return;
    }
    if (isChunkLoadFailure(event.reason)) {
      this.markRemote();
    }
  };

  private noteRemoteVersion(version: string | undefined): void {
    if (!version) {
      return;
    }
    if (!this.sessionVersion) {
      logAppUpdate('remote version pending until session freezes', version);
      this.pendingRemote = version;
      return;
    }
    if (version === this.sessionVersion) {
      logAppUpdate('remote version matches session', version);
      return;
    }
    logAppUpdate('remote version differs from session', {
      remote: version,
      session: this.sessionVersion,
    });
    this.markRemote();
  }

  /** Focus and reconnect repeat this while an update is pending; only the check repeats. */
  private markRemote(): void {
    if (!this.updateAvailable()) {
      this.updateAvailable.set(true);
      this.clearCashewCache();
      this.appUpdate.announceUpdate();
    }
    void this.appUpdate.prepare();
  }

  private alive(): boolean {
    return this.serviceId === latestServiceId;
  }

  private async fetchVersion(): Promise<string> {
    try {
      const remote = await this.readDeployment();
      if (this.isDifferentVersion(remote.version)) {
        this.clearCashewCache();
      }

      this.storeVersion(remote.version);
      this.sessionVersion = remote.version;
      this._version.set(remote.version);
      this._buildDate.set(remote.buildDate);

      if (this.pendingRemote && this.pendingRemote !== remote.version) {
        this.markRemote();
      }
      this.pendingRemote = null;

      return remote.version;
    } catch (error) {
      console.error('Failed to fetch API version:', error);
      throw error;
    }
  }

  private async readDeployment(): Promise<{
    version: string;
    buildDate: number | null;
  }> {
    const response = (await firstValueFrom(
      this.appDeploymentService.appDeploymentControllerGetByApplication({
        application: 1,
      })
    )) as AppDeploymentResponse;

    const version = response.webAppVersion;
    if (!version) {
      throw new Error('App deployment response did not include webAppVersion');
    }
    return { version, buildDate: response.webAppBuildDate ?? null };
  }

  private isDifferentVersion(version: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    const storedVersion = localStorage.getItem(
      APIVersionService.VERSION_STORAGE_KEY
    );
    if (!storedVersion) {
      return true;
    }
    return version !== storedVersion;
  }

  private storeVersion(version: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.setItem(APIVersionService.VERSION_STORAGE_KEY, version);
  }

  /**
   * Clears all Cashew HTTP cache when API version changes.
   * Removes @cache- / @ttl- / @version- keys from localStorage and sessionStorage,
   * and clears in-memory cache.
   */
  private clearCashewCache(): void {
    this.httpCacheManager.clear();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const isCashewKey = (key: string): boolean =>
      APIVersionService.CASHEW_KEY_PREFIXES.some(prefix =>
        key.startsWith(prefix)
      );

    const removeCashewKeys = (storage: Storage): void => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && isCashewKey(key)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => storage.removeItem(key));
    };

    removeCashewKeys(localStorage);
    removeCashewKeys(sessionStorage);
  }
}

function isChunkLoadFailure(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error
        ? String((error as { message: unknown }).message)
        : '';
  const name =
    error && typeof error === 'object' && 'name' in error
      ? String((error as { name: unknown }).name)
      : '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk .+ failed/.test(message) ||
    /Failed to fetch dynamically imported module/.test(message)
  );
}
